"use client";

/**
 * The country index — the page's primary tool, not a footnote.
 *
 * ── Why it looks like this ──
 * The first version buried 116 rows below three screens of preamble, wrapped in
 * a scroll-reveal, in three columns where two were near-constant: 79 rows read
 * "UNCITRAL Model Law adopters" AND "UNCITRAL MLEC table". The loudest thing on
 * screen was one link repeated 79 times.
 *
 * So: the family becomes a tinted chip, and the source column only speaks up
 * when it is SPECIFIC — a named statute we opened and verified. Generic
 * table-membership rows state their source quietly. Provenance is fully
 * preserved; it just stops shouting.
 *
 * ⚠️ NO scroll-reveal in here. Flicking to an index is how people use an index,
 * and that outruns an IntersectionObserver — you land on blank paper. Reveal
 * animations belong on marketing sections, not on reference data.
 */
import { useDeferredValue, useMemo, useState } from "react";
import Link from "next/link";
import type { LegalityCountry } from "@/lib/legality";
import { SOURCE_TABLE_CITATIONS } from "@/lib/legality";

interface FamilyOption {
  id: string;
  /** URL segment — the chip navigates to the family page. */
  slug: string;
  name: string;
  count: number;
}

interface Props {
  countries: LegalityCountry[];
  families: FamilyOption[];
}

/** Muted, distinguishable at a glance, never confetti. */
const FAMILY_CHIP: Record<string, string> = {
  eidas: "bg-[#e8eefc] text-[#31508f]",
  "uncitral-model": "bg-[#eceae4] text-[#5f5a4e]",
  "united-states": "bg-[#e7f0ec] text-[#356b55]",
  "commonwealth-eta": "bg-[#f3e9e4] text-[#8a5b45]",
  "civil-law-tiered": "bg-[#efe8f3] text-[#66508a]",
  "licensed-provider": "bg-[#fdf0dc] text-[#8a6320]",
};

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function CountryIndex({ countries, families }: Props) {
  const [query, setQuery] = useState("");
  const [familyId, setFamilyId] = useState("all");
  // 116 rows re-filter on every keystroke; defer keeps typing smooth.
  const deferredQuery = useDeferredValue(query);

  const familyById = useMemo(
    () => new Map(families.map((f) => [f.id, f])),
    [families],
  );

  const visible = useMemo(() => {
    const q = deferredQuery.trim().toLowerCase();
    return countries.filter((c) => {
      if (familyId !== "all" && c.familyId !== familyId) return false;
      if (!q) return true;
      return c.name.toLowerCase().includes(q) || c.iso2.toLowerCase() === q;
    });
  }, [countries, deferredQuery, familyId]);

  const groups = useMemo(() => {
    const map = new Map<string, LegalityCountry[]>();
    for (const c of visible) {
      const letter = c.name[0].toUpperCase();
      const bucket = map.get(letter);
      if (bucket) bucket.push(c);
      else map.set(letter, [c]);
    }
    return map;
  }, [visible]);

  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[236px_1fr] lg:gap-11">
      {/* ── Rail ── */}
      <aside className="lg:sticky lg:top-6 lg:self-start">
        <label htmlFor="country-search" className="sr-only">
          Find a country
        </label>
        <input
          id="country-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          // ⚠️ Generic on purpose. This previously read "Poland, Mexico,
          // Guatemala…" — the exact three countries one prospect had asked
          // about. Seeing your own enquiry echoed back as placeholder text
          // makes a public reference page look privately tailored, which is
          // unsettling rather than attentive.
          placeholder={`Search ${countries.length} countries…`}
          className="w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-slate-400 focus:border-[var(--color-gold)] focus:outline-none focus:ring-2 focus:ring-[var(--color-gold-muted)]"
        />

        <p className="mt-5 mb-2 text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-400 lg:mt-6">
          Legal family
        </p>
        {/* Mobile: one swipeable row. Wrapping put three rows of pills between
            the search box and the first country. Desktop: a vertical list. */}
        <div className="-mx-6 flex snap-x gap-1.5 overflow-x-auto px-6 pb-1 sm:mx-0 sm:px-0 lg:block lg:space-y-0.5 lg:overflow-visible">
          <FamilyButton
            label="All"
            count={countries.length}
            active={familyId === "all"}
            onClick={() => setFamilyId("all")}
          />
          {families.map((f) => (
            <FamilyButton
              key={f.id}
              label={f.name}
              count={f.count}
              active={familyId === f.id}
              onClick={() => setFamilyId(f.id)}
            />
          ))}
        </div>

        <p className="mt-6 hidden border-t border-slate-200 pt-4 text-[11.5px] leading-relaxed text-slate-400 lg:block">
          Countries marked with a gold rule have a full page of their own. On
          every row the family and the source are both links &mdash; follow
          either one.
        </p>
      </aside>

      {/* ── Index ── */}
      <div className="min-w-0">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="font-display text-lg font-bold tracking-tight text-ink">
            The index
          </h3>
          <span className="text-xs tabular-nums text-slate-500" aria-live="polite">
            {visible.length} of {countries.length}
          </span>
        </div>

        {/* Sticky, and on mobile a single swipeable row: wrapping to two rows
            ate vertical space on the axis that is already scarce. Targets are
            padded to stay tappable. */}
        <nav
          aria-label="Jump to letter"
          className="sticky top-0 z-10 -mx-6 flex gap-0.5 overflow-x-auto bg-gradient-to-b from-paper from-70% to-transparent px-6 py-2 sm:-mx-1 sm:flex-wrap sm:overflow-visible sm:px-1 sm:py-2.5"
        >
          {ALPHABET.map((letter) =>
            groups.has(letter) ? (
              <a
                key={letter}
                href={`#letter-${letter}`}
                className="shrink-0 rounded px-2 py-1.5 text-[12px] tabular-nums text-slate-400 transition-colors hover:bg-ink hover:text-white sm:px-1.5 sm:py-0.5 sm:text-[11.5px]"
              >
                {letter}
              </a>
            ) : (
              <span
                key={letter}
                aria-hidden="true"
                className="shrink-0 px-2 py-1.5 text-[12px] tabular-nums text-slate-300 opacity-40 sm:px-1.5 sm:py-0.5 sm:text-[11.5px]"
              >
                {letter}
              </span>
            ),
          )}
        </nav>

        {visible.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <p className="text-sm font-medium text-slate-600">
              No country here matches &ldquo;{query}&rdquo;
            </p>
            <p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-slate-500">
              This guide lists countries we can trace to a published source. Absence
              is not a statement that electronic signatures are unrecognized there
              &mdash; only that we have not yet sourced it.
            </p>
          </div>
        ) : (
          [...groups.entries()].map(([letter, rows]) => (
            <section key={letter}>
              <div
                id={`letter-${letter}`}
                className="mt-5 mb-0.5 flex scroll-mt-16 items-center gap-3"
              >
                <span className="w-4 font-editorial text-[15px] font-bold text-ink">
                  {letter}
                </span>
                <span className="h-px flex-1 bg-slate-200" aria-hidden="true" />
              </div>
              <ul>
                {rows.map((c) => (
                  <CountryRow
                    key={c.iso2}
                    country={c}
                    family={familyById.get(c.familyId)}
                  />
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </div>
  );
}

function FamilyButton({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex w-auto shrink-0 items-center justify-between gap-2.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-left text-[13.5px] transition-colors lg:w-full lg:whitespace-normal ${
        active
          ? "bg-ink text-white"
          : "text-slate-700 hover:bg-[var(--color-paper-dark)]"
      }`}
    >
      <span>{label}</span>
      <span className={`text-[11.5px] tabular-nums ${active ? "text-white/55" : "text-slate-400"}`}>
        {count}
      </span>
    </button>
  );
}

function CountryRow({
  country,
  family,
}: {
  country: LegalityCountry;
  family?: FamilyOption;
}) {
  const citation = SOURCE_TABLE_CITATIONS[country.sourceTable];
  const isFull = country.depth === "full";
  // A named statute is specific and worth reading. Table membership is the same
  // sentence 79 times — state it, quietly.
  const specific = Boolean(country.statuteName && country.statuteUrl);
  // 107 of 116 rows are table-membership. Their citation lives on the table,
  // and it must be REACHABLE — the guide's whole claim is that every row links
  // to the source that placed it there.
  const genericHref = citation.sources[0]?.url;

  return (
    <li
      className={`grid grid-cols-1 items-baseline gap-x-4 gap-y-0.5 rounded-md py-1.5 pl-3 pr-2.5 hover:bg-white sm:gap-y-1 sm:py-2 sm:grid-cols-[1fr_150px_minmax(150px,280px)] ${
        isFull ? "shadow-[inset_2px_0_0_var(--color-gold)]" : ""
      }`}
    >
      <div className="text-sm text-ink">
        {isFull && country.variantSlug ? (
          <Link
            href={`/${country.variantSlug}`}
            className="font-medium text-ink underline decoration-[var(--color-gold)] decoration-[1.5px] underline-offset-[3px] hover:bg-[var(--color-gold-muted)]"
          >
            {country.name}
            <span aria-hidden="true" className="ml-0.5 text-[12px] text-[var(--color-gold)]">
              ↗
            </span>
          </Link>
        ) : (
          country.name
        )}
        <span className="ml-1.5 text-[10.5px] tracking-wider text-slate-300">
          {country.iso2}
        </span>
      </div>

      {/* Mobile: chip and source share one line, so a row is 2 lines not 3 —
          at 116 rows that removed ~3,000px of scroll. `sm:contents` dissolves
          this wrapper at sm+, leaving the desktop grid exactly as it was. */}
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 sm:contents">
      {/* The chip navigates. It names a thing that has its own page, and a
          chip that looks tappable but does nothing is a dead end — reported
          from the live page. Filtering is already one click away in the rail. */}
      {family ? (
        <Link
          href={`/electronic-signature-legality/${family.slug}`}
          title={`${family.name} — how this framework works`}
          className={`justify-self-start rounded-full px-2 py-0.5 text-[11px] transition-shadow hover:shadow-[0_0_0_1.5px_rgba(15,23,42,0.25)] ${
            FAMILY_CHIP[country.familyId] ?? "bg-slate-100 text-slate-600"
          }`}
        >
          {family.name}
        </Link>
      ) : (
        <span className="justify-self-start rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
          {country.familyId}
        </span>
      )}

      <div className="text-xs leading-snug">
        {specific ? (
          <a
            href={country.statuteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-500 underline decoration-slate-300 decoration-dotted underline-offset-2 hover:text-ink"
          >
            {country.statuteName}
          </a>
        ) : (
          <a
            href={genericHref}
            target="_blank"
            rel="noopener noreferrer"
            title={citation.label}
            className="text-slate-400 underline decoration-slate-200 decoration-dotted underline-offset-2 hover:text-ink"
          >
            {citation.shortLabel}
          </a>
        )}
      </div>
      </div>
    </li>
  );
}
